import React, {
    ReactNode,
    createContext,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';

interface IAutoFillFocusValue {
    input: { isFocused: boolean };
    mouse: { current: { isWithinListbox: boolean } };
    setFocusedToTrue(): void;
    setFocusedToFalse(): void;
    setMouseIsWithinListboxToTrue(): void;
    setMouseIsWithinListboxToFalse(): void;
    autoFillFocusOnBlurHandler(): void;
    autoFillFocusOnFocusHandler(): void;
}

const AutoFillFocusContext = createContext<IAutoFillFocusValue>({
    input: { isFocused: false },
    mouse: { current: { isWithinListbox: true } },
    setFocusedToTrue: () => {},
    setFocusedToFalse: () => {},
    setMouseIsWithinListboxToTrue: () => {},
    setMouseIsWithinListboxToFalse: () => {},
    autoFillFocusOnBlurHandler: () => {},
    autoFillFocusOnFocusHandler: () => {},
});

export default function AutoFillFocusProvider({
    children,
}: {
    children: ReactNode;
}) {
    const [input, setInput] = useState<{ isFocused: boolean }>({
        isFocused: false,
    });
    const setFocusedToTrue = useRef<() => void>();
    const setFocusedToFalse = useRef<() => void>();

    const mouse = useRef({ isWithinListbox: false });
    const setMouseIsWithinListboxToTrue = useRef<() => void>();
    const setMouseIsWithinListboxToFalse = useRef<() => void>();

    const autoFillFocusOnBlurHandler = useRef<() => void>();
    const autoFillFocusOnFocusHandler = useRef<() => void>();

    useMemo(() => {
        setFocusedToTrue.current = () => setInput({ isFocused: true });
        setFocusedToFalse.current = () => setInput({ isFocused: false });
        setMouseIsWithinListboxToTrue.current = () => {
            mouse.current = { isWithinListbox: true };
        };
        setMouseIsWithinListboxToFalse.current = () => {
            mouse.current = { isWithinListbox: false };
        };
        autoFillFocusOnFocusHandler.current = () => {
            setFocusedToTrue.current!();
        };
    }, []);

    useMemo(() => {
        autoFillFocusOnBlurHandler.current = () => {
            if (input.isFocused) {
                if (mouse.current.isWithinListbox === false) {
                    setFocusedToFalse.current!();
                }
            }
        };
    }, [input]);

    /**
     * by memoizing these values into an object, rerendering is prevented on focus, blur, mouseleave, and mouseenter
     */
    const contextValue = useMemo(() => {
        return {
            input,
            mouse,
            setFocusedToTrue: setFocusedToTrue.current,
            setFocusedToFalse: setFocusedToFalse.current,
            setMouseIsWithinListboxToTrue:
                setMouseIsWithinListboxToTrue.current,
            setMouseIsWithinListboxToFalse:
                setMouseIsWithinListboxToFalse.current,
            autoFillFocusOnBlurHandler: autoFillFocusOnBlurHandler.current,
            autoFillFocusOnFocusHandler: autoFillFocusOnFocusHandler.current,
        } as IAutoFillFocusValue;
    }, [input]);

    return (
        <AutoFillFocusContext.Provider value={contextValue}>
            {children}
        </AutoFillFocusContext.Provider>
    );
}

/**
 * Necessary for managing when the `input` rerenders so that it does not interfere
 * with event handling or events being triggered for the `listBox`
 */
export const useAutoFillFocus = () => useContext(AutoFillFocusContext);
